import React from 'react';
import { times } from 'lodash';
import I18n from 'i18n-js';
import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import { RCImage, RCIcon, useMediaQuery } from '@reverbdotcom/cadence/components';
import Tiles from '@reverbdotcom/commons/src/components/tiles';
import { MagnifyingGlassIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import classNames from 'classnames';
import { reverb_personalization_SearchQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { V2_MARKETPLACE_PATH } from '../shared/constants';

interface IProps {
  querySuggestions?: reverb_personalization_SearchQuery[];
  loading?: boolean;
  displayPills?: boolean;
  displayLinks?: boolean;
  relatedSearchLocation: 'top' | 'bottom';
}

interface QuerySuggestionProps {
  querySuggestions?: reverb_personalization_SearchQuery[];
  loading?: boolean;
  relatedSearchLocation: 'top' | 'bottom';
}

const COMPONENT_NAME = 'MarketplaceRelatedSearches';
const MAX_SEARCHES = 7;

function backgroundImageStyle(suggestion) {
  let imageUrl;
  if (suggestion.listings) {
    imageUrl = suggestion.listings[0].images[0]?.source;
  }
  return imageUrl;
}

function buildClickEvent(querySuggestion, position, displayStyle, relatedSearchLocation) {
  return {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedRelatedSearchSuggestion,
    query: querySuggestion.query,
    relatedSearchLocation,
    displayStyle,
    position,
  };
}

function renderPlaceholder(displayPills, idx = null, displayLinks = false) {
  const classes = classNames(
    {
      'related-search-pill': displayPills,
      'related-search-pill--placeholder': displayPills,
      'related-search-image-card': !displayPills,
      'related-search-image-card--placeholder': !displayPills,
      'related-search-link': displayLinks,
      'related-search-link--placeholder': displayLinks,
    },
  );

  if (displayPills) {
    return (
      <div className={classes} key={`related-search-pill__placeholder-${idx}`}>
        <div className="related-search-pill__text" key={`related-search-pill__placeholder-text-${idx}`} />
      </div>
    );
  }

  if (displayLinks) {
    return (
      <div className={classes} key={`related-search-link__placeholder-${idx}`}>
        <span className="related-search-link__text" key={`related-search-link__placeholder-text-${idx}`} />
      </div>
    );
  }

  return (
    <div className={classes} key={`related-search-image-card__placeholder-${idx}`}>
      <div className="related-search-image-card__image-container" key={`related-search-image-card__placeholder-image-container-${idx}`}>
        <div className="related-search-image-card__image" />
      </div>
    </div>
  );
}

function QuerySuggestion({ suggestion, idx, displayPills, displayLinks, relatedSearchLocation }) {
  const url = useURL();
  const urlParams = {
    query: suggestion.query,
    referer: 'related_search',
  };

  Object.keys(urlParams).forEach(k => !urlParams[k] && delete urlParams[k]);

  let path = Paths.marketplace.expand(urlParams);
  if (url.pathname.includes(V2_MARKETPLACE_PATH)) {
    path = Paths.marketplaceV2.expand(urlParams);
  }

  if (displayLinks) {
    return (
      <CoreLink
        clickEvent={buildClickEvent(suggestion, idx, 'link', relatedSearchLocation)}
        className="related-search-link"
        to={path}
        onClick={() => window.scrollTo(0, 0)}
        key={`related-search-link-${suggestion.query}-${idx}`}
      >
        <RCIcon svgComponent={MagnifyingGlassIcon} size="rc-space-3" />
        <span className="related-search-link__text">
          {suggestion?.query}
        </span>
      </CoreLink>
    );
  }

  if (displayPills) {
    return (
      <CoreLink
        clickEvent={buildClickEvent(suggestion, idx, 'pill', relatedSearchLocation)}
        className="related-search-pill"
        to={path}
        onClick={() => window.scrollTo(0, 0)}
        key={`related-search-pill-link-${suggestion.query}-${idx}`}
      >
        <div className="related-search-pill__icon">
          <RCIcon svgComponent={MagnifyingGlassIcon} inline />
        </div>
        <div className="related-search-pill__text" key={`related-search-pill__text-${suggestion.query}-${idx}`}>
          {suggestion?.query}
        </div>
      </CoreLink>
    );
  }

  return (
    <CoreLink
      className="related-search-image-card"
      clickEvent={buildClickEvent(suggestion, idx, 'image', relatedSearchLocation)}
      to={path}
      onClick={() => window.scrollTo(0, 0)}
      key={`related-search-image-link-${suggestion.query}-${idx}`}
    >
      <div
        className="related-search-image-card__image-container"
        key={`related-search-image-card-photo-link-${suggestion.query}-${idx}`} >
        <RCImage
          src={backgroundImageStyle(suggestion)}
          overlay="offwhite"
          borderRadius="full"
        />
      </div>
      <div className="related-search-image-card__text" key={`related-search-image-card-text-${suggestion.query}-${idx}`}>
        {suggestion?.query}
      </div>
    </CoreLink>
  );
}

export function RelatedSearchImageCard({
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: QuerySuggestionProps) {
  return (
    <div className="pt-10">
      <OverflowingRow
        title={
          <div className="related-search-image-card__title" key="related-search-image-card-title">
            {I18n.t('discovery.marketplace.querySuggestions.relatedSearches')}
          </div>
        }
        collectionCount={querySuggestions?.length}
        centeredTitle={!useMediaQuery('mobile')}
      >
        <Tiles
          loading={loading}
          placeholder={renderPlaceholder(false)}
          centered
          singleRow
        >
          {!!querySuggestions?.length &&
            querySuggestions?.map((qs, idx) => {
              return (
                <QuerySuggestion
                  key={`marketplace-related-search-image-card-${idx}`}
                  suggestion={qs}
                  idx={idx}
                  displayPills={false}
                  displayLinks={false}
                  relatedSearchLocation={relatedSearchLocation}
                />
              );
            })}
        </Tiles>
      </OverflowingRow>

    </div>
  );
}

export function RelatedSearchPills({
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: QuerySuggestionProps) {

  return (
    <div className="related-search-pill__container" key="related-search-pills-container">
      <div className="related-search-pill__title" key="related-search-pills-title">
        {I18n.t('discovery.marketplace.querySuggestions.relatedSearches')}
      </div>
      <div className="related-searches-pills-row" key="related-search-pills-row">
        {!loading && querySuggestions?.map((qs, idx) => {
          return (
            <QuerySuggestion
              key={`marketplace-related-search-pill-${qs?.query}-${idx}`}
              suggestion={qs}
              idx={idx}
              relatedSearchLocation={relatedSearchLocation}
              displayPills
              displayLinks={false}
            />
          );
        })}
        {loading && times(MAX_SEARCHES, (idx) => { return renderPlaceholder(true, idx); })}
      </div>
    </div>
  );
}


export function RelatedSearchLinks({
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: QuerySuggestionProps) {
  const isMobile = useMediaQuery('mobile');

  return (
    <div className="related-search-links__container" key="related-search-links-container">
      <div className="related-search-links__title" key="related-search-links-title">
        {isMobile
          ? I18n.t('discovery.marketplace.querySuggestions.related')
          : I18n.t('discovery.marketplace.querySuggestions.relatedSearches')}:
      </div>
      <div className="related-search-links-row" key="related-search-links-row">
        {!loading && querySuggestions?.map((qs, idx) => {
          return (
            <QuerySuggestion
              key={`marketplace-related-search-link-${qs?.query}-${idx}`}
              suggestion={qs}
              idx={idx}
              relatedSearchLocation={relatedSearchLocation}
              displayPills={false}
              displayLinks
            />
          );
        })}
        {loading && times(MAX_SEARCHES, (idx) => { return renderPlaceholder(false, idx, true); })}
      </div>
    </div>
  );
}


export function MarketplaceRelatedSearches({
  displayPills = false,
  displayLinks = false,
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: IProps) {
  const [showChild, setShowChild] = React.useState(false);
  React.useEffect(() => {
    setShowChild(true);
  }, []);

  if (!loading && !querySuggestions?.length) { return null; }

  const classes = classNames(
    'related-searches-row',
    {
      'related-searches-pills': displayPills,
      'related-searches-links': displayLinks,
      'related-searches-image-cards': !displayPills && !displayLinks,
    },
  );

  if (!showChild) {
    if (displayLinks) {
      return (
        <div className={classes} key={`${relatedSearchLocation}-links-related-searches-row-placeholder`}>
          <RelatedSearchLinks
            loading
            querySuggestions={[]}
            relatedSearchLocation={relatedSearchLocation}
          />
        </div>
      );
    }

    if (displayPills) {
      return (
        <div className={classes} key={`${relatedSearchLocation}-pills-related-searches-row-placeholder`}>
          <RelatedSearchPills
            loading
            querySuggestions={[]}
            relatedSearchLocation={relatedSearchLocation}
          />
        </div>
      );
    }

    return (
      <div className={classes} key={`${relatedSearchLocation}-image-cards-related-searches-row-placeholder`}>
        <RelatedSearchImageCard
          querySuggestions={[]}
          relatedSearchLocation={relatedSearchLocation}
          loading
        />
      </div>
    );
  }

  function renderContent() {
    if (displayLinks) {
      return (
        <RelatedSearchLinks
          loading={loading}
          querySuggestions={querySuggestions}
          relatedSearchLocation={relatedSearchLocation}
        />
      );
    }
    if (displayPills) {
      return (
        <RelatedSearchPills
          loading={loading}
          querySuggestions={querySuggestions}
          relatedSearchLocation={relatedSearchLocation}
        />
      );
    }
    return (
      <RelatedSearchImageCard
        loading={loading}
        querySuggestions={querySuggestions}
        relatedSearchLocation={relatedSearchLocation}
      />
    );
  }

  return (
    <div
      className={classes}
      key={`${relatedSearchLocation}-${displayPills ? 'pills' : displayLinks ? 'links' : 'image-cards'}-related-searches-row`}
    >
      {renderContent()}
    </div>
  );
}

export default MarketplaceRelatedSearches;
